import { useCallback, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FormatColorFillIcon, StabiloIcon } from "@l2r-front/l2r-icons"
import { DEFAULT_SIGN_ICON } from "@l2r-front/l2r-map"
import { useIsRoadWritable, useNetworksStateContext } from "@l2r-front/l2r-networks"
import {
    CatalogList,
    CatalogListError,
    CatalogListSkeleton,
} from "@l2r-front/l2r-ui"
import { CatalogItem, getFinalCatalogTypes } from "@l2r-front/l2r-utils"

import { API_MODULE_ROADSIDE } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRoadsideDispatchContext, useRoadsideStateContext } from "../../contexts/RoadsideContext"
import { useRoadsideInventoriesBatchUpdate } from "../../hooks/mutations/useRoadsideInventoriesBatchUpdate"
import { useRoadsideInventoryTypesStats } from "../../hooks/queries/useRoadsideInventoryTypesStats"
import { useRoadsideProject } from "../../hooks/queries/useRoadsideProject"
import { useRoadsideType, useRoadsideTypes } from "../../hooks/queries/useRoadsideTypes"

import { RoadsideCount } from "../RoadsideCount"
import { RoadsidePresenceEditModal } from "../RoadsidePresenceEditModal"
import { RoadsideStateEditModal } from "../RoadsideStateEditModal"

type RoadsideTypeListProps = {
    baseRoadsideTypeCode?: string,
    className?: string,
    onChangeFilter?: (filter: { type: string }) => void,
    onCreate?: () => void,
}

export function RoadsideTypeList(props: RoadsideTypeListProps) {

    const {
        baseRoadsideTypeCode,
        className,
        onChangeFilter,
        onCreate,
    } = props

    const { t } = useTranslation()
    const { data } = useRoadsideType(baseRoadsideTypeCode)
    const { data: roadsideProject } = useRoadsideProject()
    const contextState = useRoadsideStateContext()
    const contextDispatch = useRoadsideDispatchContext()
    const { ascendantCodes, filters } = contextState
    const { road } = useParams()
    const isRoadWriter = useIsRoadWritable(road, API_MODULE_ROADSIDE)
    const { selectedSubNetworkSlugs } = useNetworksStateContext()
    const [mutationRunning, setMutationRunning] = useState(false)
    const { setFilters } = contextDispatch
    const [presenceEditingType, setPresenceEditingType] = useState<CatalogItem | null>(null)
    const [stateEditingType, setStateEditingType] = useState<CatalogItem | null>(null)

    const apiFilters = {
        ...filters?.conditions?.length && { condition: filters.conditions },
        ...(road && { linear_location_road: road }),
    }

    const { data: stats } = useRoadsideInventoryTypesStats({ ...apiFilters })

    const {
        data: roadsideTypes,
        isError: isErrorRoadsideTypes,
        isLoading: isLoadingRoadsideTypes,
        isFetching: isFetchingRoadsideTypes,
    } = useRoadsideTypes(baseRoadsideTypeCode)

    const presenceUpdateMutation = useRoadsideInventoriesBatchUpdate({}, {
        onSuccess: () => {
            setMutationRunning(false)
            setPresenceEditingType(null)
        },
        onError: () => {
            setMutationRunning(false)
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideTypeList.presenceSnackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideTypeList.presenceSnackbar.success"),
    })

    const stateUpdateMutation = useRoadsideInventoriesBatchUpdate({}, {
        onSuccess: () => {
            setMutationRunning(false)
            setStateEditingType(null)
        },
        onError: () => {
            setMutationRunning(false)
        },
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideTypeList.stateSnackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideTypeList.stateSnackbar.success"),
    })

    const title = data?.name ?? t(I18N_NAMESPACE, "containers.roadsideTypeList.defaultTitle")

    const applyFilter = useCallback((newFilter) => {
        if (filters.type === newFilter || ascendantCodes.indexOf(newFilter) >= 0) {
            const filterIndex = ascendantCodes.indexOf(newFilter)
            if (filterIndex !== ascendantCodes.length - 1) {
                setFilters({ type: ascendantCodes[filterIndex + 1] })
            } else {
                setFilters({ type: null })
            }
        } else {
            setFilters({ type: newFilter })
            onChangeFilter?.({ type: newFilter })
        }
    }, [filters, ascendantCodes, setFilters, onChangeFilter])

    const onSavePresenceData = useCallback((roadsideInventories, parentType) => {
        presenceUpdateMutation.mutate({
            road: road,
            roadside_inventory_type_ancestor: parentType,
            roadside_inventories: roadsideInventories,
            project: roadsideProject?.uuid,
            subnetworks: selectedSubNetworkSlugs,
        } as unknown as void)
        setMutationRunning(true)
    }, [presenceUpdateMutation, road, roadsideProject, selectedSubNetworkSlugs])

    const onSaveStateData = useCallback((roadsideInventories, parentType) => {
        stateUpdateMutation.mutate({
            road: road,
            roadside_inventory_type_ancestor: parentType,
            roadside_inventories: roadsideInventories,
            project: roadsideProject?.uuid,
            subnetworks: selectedSubNetworkSlugs,
        } as unknown as void)
        setMutationRunning(true)
    }, [stateUpdateMutation, road, roadsideProject, selectedSubNetworkSlugs])

    const moreMenuConfigs = (!!road && isRoadWriter && roadsideTypes) ? getFinalCatalogTypes(roadsideTypes)
        .filter(v => filters?.type === v.code)
        .reduce((acc, v) => ({
            ...acc,
            [v.code]: [
                {
                    displayed: true,
                    enabled: stats?.length?.[v.code] > 0,
                    id: "edit-condition",
                    label: t(I18N_NAMESPACE, "containers.roadsideTypeList.editCondition"),
                    icon: <FormatColorFillIcon color="common/black_states" />,
                    props: {
                        onClick: () => {
                            setFilters({ type: v.code })
                            setStateEditingType(v)
                        },
                    }
                },
                {
                    displayed: true,
                    enabled: true,
                    id: "edit-location",
                    label: t(I18N_NAMESPACE, "containers.roadsideTypeList.editLocation"),
                    icon: <StabiloIcon color="common/black_states" />,
                    props: {
                        onClick: () => {
                            setFilters({ type: v.code })
                            setPresenceEditingType(v)
                        },
                    }
                },
            ]
        }), {}) : {}

    if (isErrorRoadsideTypes && !isFetchingRoadsideTypes) {
        return <CatalogListError className={className} errorText={t(I18N_NAMESPACE, "components.roadsideTypeList.error")} />
    }

    if (isLoadingRoadsideTypes) {
        return <CatalogListSkeleton className={className} />
    }

    return <>
        <CatalogList
            alwaysDisplayIconsWhenAvailable
            onClick={applyFilter}
            baseItemCode={baseRoadsideTypeCode}
            ascendantCodes={ascendantCodes}
            className={className}
            data={roadsideTypes}
            defaultIcon={DEFAULT_SIGN_ICON}
            emptyListText={t(I18N_NAMESPACE, "components.roadsideTypeList.emptyList")}
            filter={filters?.type}
            ItemsCountComponent={RoadsideCount}
            moreMenuConfigs={moreMenuConfigs}
            onCreate={isRoadWriter ? onCreate : null}
            title={title}

        />
        {!!stateEditingType && <RoadsideStateEditModal
            open={!!stateEditingType}
            type={stateEditingType}
            onClose={() => setStateEditingType(null)}
            onSave={onSaveStateData}
            showLoader={mutationRunning}
        />}
        {!!presenceEditingType && <RoadsidePresenceEditModal
            open={!!presenceEditingType}
            type={presenceEditingType}
            onSave={onSavePresenceData}
            onClose={() => setPresenceEditingType(null)}
            showLoader={mutationRunning}
        />}
    </>
}